import React, { useEffect, useState } from "react";
import { api, apiErr, fmtMoney, fmtDate } from "../lib/api";
import { LOGO_URL } from "../lib/permissions";
import { PageHeader, StatusBadge } from "../components/ResourcePage";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Trash2, Receipt, Printer, ShoppingCart, X } from "lucide-react";
import { CustomerPicker } from "../components/CustomerPicker";
import { toast } from "sonner";

function printReceipt(sale) {
  const rows = (sale.items || []).map(
    (i) => `<tr><td>${i.name}</td><td style="text-align:center">${i.quantity}</td><td style="text-align:right">${fmtMoney(i.unit_price)}</td><td style="text-align:right">${fmtMoney(i.quantity * i.unit_price)}</td></tr>`
  ).join("");
  const html = `<!doctype html><html><head><title>${sale.receipt_number}</title>
  <style>body{font-family:Arial,sans-serif;color:#1A1D1A;max-width:480px;margin:24px auto;padding:0 16px}
  .head{text-align:center;border-bottom:2px solid #2C5530;padding-bottom:12px;margin-bottom:16px}
  img{width:72px;height:72px;border-radius:50%}
  h1{font-size:20px;margin:8px 0 2px}.muted{color:#5C665E;font-size:12px;margin:1px 0}
  table{width:100%;border-collapse:collapse;font-size:13px;margin-top:8px}
  th,td{padding:6px 4px;border-bottom:1px solid #eee}th{text-align:left;color:#5C665E;font-size:11px;text-transform:uppercase}
  .tot{display:flex;justify-content:space-between;font-weight:bold;font-size:16px;margin-top:12px;padding-top:8px;border-top:2px solid #2C5530}
  .foot{text-align:center;color:#5C665E;font-size:11px;margin-top:24px}</style></head>
  <body><div class="head"><img src="${LOGO_URL}"/><h1>Modav Farms</h1>
  <p class="muted">No. 13 Gborogun Rd, Oke Quarters, Ilese, Ogun State</p>
  <p class="muted">+234 7061797432 · Modavfarms@gmail.com</p></div>
  <p class="muted"><b>Receipt:</b> ${sale.receipt_number}</p>
  <p class="muted"><b>Date:</b> ${fmtDate(sale.date)}</p>
  <p class="muted"><b>Customer:</b> ${sale.customer_name || "Walk-in"} ${sale.customer_phone ? "· " + sale.customer_phone : ""}</p>
  <p class="muted"><b>Payment:</b> ${sale.payment_method} (${sale.status})</p>
  <table><thead><tr><th>Item</th><th style="text-align:center">Qty</th><th style="text-align:right">Price</th><th style="text-align:right">Amount</th></tr></thead>
  <tbody>${rows}</tbody></table>
  ${sale.discount ? `<p class="muted" style="text-align:right">Discount: -${fmtMoney(sale.discount)}</p>` : ""}
  <div class="tot"><span>TOTAL</span><span>${fmtMoney(sale.total)}</span></div>
  <p class="foot">Fresh. Ethical. Sustainable.<br/>Thank you for your patronage!</p>
  <script>window.onload=function(){window.print();}</script></body></html>`;
  const w = window.open("", "_blank", "width=520,height=700");
  if (w) { w.document.write(html); w.document.close(); }
}

export function Sales() {
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState({ customer_name: "", customer_phone: "", customer_id: "" });
  const [discount, setDiscount] = useState(0);
  const [payment, setPayment] = useState("cash");
  const [open, setOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [delId, setDelId] = useState(null);
  const [pick, setPick] = useState("");

  const load = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/products"), api.get("/sales")]);
      setProducts(p.data); setSales(s.data);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const addToCart = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) return;
    const ex = cart.find((c) => c.product_id === pid);
    if (ex) setCart(cart.map((c) => c.product_id === pid ? { ...c, quantity: c.quantity + 1 } : c));
    else setCart([...cart, { product_id: p.id, name: p.name, quantity: 1, unit_price: p.price }]);
    setPick("");
  };
  const setQty = (i, q) => setCart(cart.map((c, x) => x === i ? { ...c, quantity: Number(q) } : c));
  const setPrice = (i, q) => setCart(cart.map((c, x) => x === i ? { ...c, unit_price: Number(q) } : c));
  const rm = (i) => setCart(cart.filter((_, x) => x !== i));

  const subtotal = cart.reduce((s, c) => s + c.quantity * c.unit_price, 0);
  const total = subtotal - Number(discount || 0);

  const openNew = () => { setCart([]); setCustomer({ customer_name: "", customer_phone: "", customer_id: "" }); setDiscount(0); setPayment("cash"); setOpen(true); };

  const submit = async () => {
    if (cart.length === 0) { toast.error("Add at least one product"); return; }
    try {
      const { data } = await api.post("/sales", {
        customer_name: customer.customer_name || "Walk-in customer",
        customer_phone: customer.customer_phone, customer_id: customer.customer_id || "",
        items: cart, discount: Number(discount || 0),
        amount_paid: total, payment_method: payment, status: "paid",
      });
      toast.success(`Sale recorded · ${data.receipt_number}`);
      setOpen(false); setReceipt(data); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/sales/${delId}`); toast.success("Deleted"); setDelId(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="fade-up">
      <PageHeader title="Sales & Receipts" subtitle="Record farm-gate sales and issue digital receipts."
        action={<Button onClick={openNew} data-testid="sale-new"><ShoppingCart className="mr-1.5 h-4 w-4" />New Sale</Button>} />

      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Receipt</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sales.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="py-12 text-center text-muted-foreground">No sales yet. Click New Sale.</TableCell></TableRow>
            ) : sales.map((s) => (
              <TableRow key={s.id} data-testid="sale-row">
                <TableCell className="font-semibold">{s.receipt_number}</TableCell>
                <TableCell className="text-sm">{fmtDate(s.date)}</TableCell>
                <TableCell className="text-sm">{s.customer_name}</TableCell>
                <TableCell className="text-sm capitalize">{s.payment_method}</TableCell>
                <TableCell><StatusBadge value={s.status} /></TableCell>
                <TableCell className="text-right num font-semibold">{fmtMoney(s.total)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setReceipt(s)} data-testid="sale-view"><Receipt className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => printReceipt(s)} data-testid="sale-print"><Printer className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDelId(s.id)} data-testid="sale-delete"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* New sale dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New Sale</DialogTitle>
            <DialogDescription>Add products, set the customer and payment, then record the sale.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs">Customer</Label>
              <div className="mt-1.5">
                <CustomerPicker name={customer.customer_name} customerId={customer.customer_id}
                  onChange={(name, id, obj) => setCustomer({ customer_name: name, customer_id: id, customer_phone: obj?.phone || customer.customer_phone })}
                  testid="sale-customer" placeholder="Walk-in customer" />
              </div></div>
            <div><Label className="text-xs">Customer phone</Label>
              <Input className="mt-1.5" value={customer.customer_phone} onChange={(e) => setCustomer({ ...customer, customer_phone: e.target.value })} data-testid="sale-phone" /></div>
          </div>

          <div className="mt-2">
            <Label className="text-xs">Add product</Label>
            <Select value={pick} onValueChange={addToCart}>
              <SelectTrigger className="mt-1.5" data-testid="sale-add-product"><SelectValue placeholder="Select a product…" /></SelectTrigger>
              <SelectContent>
                {products.filter((p) => p.active !== false).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} — {fmtMoney(p.price)}/{p.unit}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-2 space-y-2">
            {cart.length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Cart is empty.</p>}
            {cart.map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid="cart-item">
                <span className="flex-1 truncate text-sm font-medium">{c.name}</span>
                <Input type="number" className="w-16" value={c.quantity} onChange={(e) => setQty(i, e.target.value)} data-testid={`cart-qty-${i}`} />
                <Input type="number" className="w-28" value={c.unit_price} onChange={(e) => setPrice(i, e.target.value)} />
                <span className="num w-28 text-right text-sm">{fmtMoney(c.quantity * c.unit_price)}</span>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => rm(i)}><X className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>

          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs">Discount (₦)</Label>
              <Input type="number" className="mt-1.5" value={discount} onChange={(e) => setDiscount(e.target.value)} /></div>
            <div><Label className="text-xs">Payment method</Label>
              <Select value={payment} onValueChange={setPayment}>
                <SelectTrigger className="mt-1.5" data-testid="sale-payment"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["cash", "transfer", "pos", "credit"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
                </SelectContent>
              </Select></div>
          </div>

          <div className="mt-3 flex items-center justify-between rounded-md bg-muted px-4 py-3">
            <span className="text-sm font-medium">Total</span>
            <span className="num font-display text-xl font-bold" data-testid="sale-total">{fmtMoney(total)}</span>
          </div>

          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="sale-save">Record sale & issue receipt</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt view */}
      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="sr-only">Receipt</DialogTitle>
            <DialogDescription className="sr-only">Digital receipt</DialogDescription>
          </DialogHeader>
          {receipt && (
            <div data-testid="receipt-view">
              <div className="border-b-2 border-primary pb-3 text-center">
                <img src={LOGO_URL} alt="" className="mx-auto h-16 w-16 rounded-full" />
                <h2 className="font-display text-lg font-extrabold">Modav Farms</h2>
                <p className="text-[11px] text-muted-foreground">No. 13 Gborogun Rd, Ilese, Ogun State</p>
                <p className="text-[11px] text-muted-foreground">+234 7061797432</p>
              </div>
              <div className="mt-3 space-y-0.5 text-sm">
                <p><span className="text-muted-foreground">Receipt:</span> <b>{receipt.receipt_number}</b></p>
                <p><span className="text-muted-foreground">Date:</span> {fmtDate(receipt.date)}</p>
                <p><span className="text-muted-foreground">Customer:</span> {receipt.customer_name} {receipt.customer_phone && `· ${receipt.customer_phone}`}</p>
                <p className="capitalize"><span className="text-muted-foreground">Payment:</span> {receipt.payment_method} ({receipt.status})</p>
              </div>
              <table className="mt-3 w-full text-sm">
                <thead><tr className="border-b text-xs uppercase text-muted-foreground"><th className="py-1 text-left">Item</th><th className="text-center">Qty</th><th className="text-right">Amount</th></tr></thead>
                <tbody>
                  {receipt.items.map((i, x) => (
                    <tr key={x} className="border-b border-border/60"><td className="py-1.5">{i.name}</td><td className="text-center">{i.quantity}</td><td className="num text-right">{fmtMoney(i.quantity * i.unit_price)}</td></tr>
                  ))}
                </tbody>
              </table>
              {receipt.discount > 0 && <p className="mt-1 text-right text-sm text-muted-foreground">Discount: -{fmtMoney(receipt.discount)}</p>}
              <div className="mt-2 flex items-center justify-between border-t-2 border-primary pt-2">
                <span className="font-semibold">TOTAL</span>
                <span className="num font-display text-xl font-extrabold">{fmtMoney(receipt.total)}</span>
              </div>
              <Button className="mt-4 w-full" onClick={() => printReceipt(receipt)} data-testid="receipt-print"><Printer className="mr-1.5 h-4 w-4" />Print / Download</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this sale?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive" data-testid="sale-confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
